import { useState } from "react";
import styled from "styled-components";
import { submitContactMessage } from "../../services/contactServices";

const Card = styled.div`
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.xl};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding: 2rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.375rem;
  color: hsl(${({ theme }) => theme.colors.foreground});
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  background: hsl(${({ theme }) => theme.colors.background});
  color: hsl(${({ theme }) => theme.colors.foreground});
  outline: none;
  &:focus-visible {
    box-shadow: 0 0 0 2px hsl(${({ theme }) => theme.colors.ring} / 0.35);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 0.625rem 0.75rem;
  font-size: 0.875rem;
  min-height: 8rem;
  margin-bottom: 1rem;
  font-family: inherit;
  resize: vertical;
  background: hsl(${({ theme }) => theme.colors.background});
  color: hsl(${({ theme }) => theme.colors.foreground});
  outline: none;
  &:focus-visible {
    box-shadow: 0 0 0 2px hsl(${({ theme }) => theme.colors.ring} / 0.35);
  }
`;

const Grid2 = styled.div`
  display: grid;
  gap: 1rem;
  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Submit = styled.button`
  width: 100%;
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  font-weight: 600;
  font-size: 0.875rem;
  &:hover {
    opacity: 0.92;
  }
  &:disabled {
    opacity: 0.55;
  }
`;

const Note = styled.p`
  margin: 0.75rem 0 0;
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  text-align: center;
`;

const Success = styled.p`
  margin: 0 0 1rem;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: hsl(${({ theme }) => theme.colors.accent} / 0.12);
  color: hsl(${({ theme }) => theme.colors.foreground});
  font-size: 0.875rem;
`;

const Err = styled.p`
  margin: 0 0 1rem;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: hsl(${({ theme }) => theme.colors.couponMaroon} / 0.08);
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
  font-size: 0.875rem;
`;

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const combined = form.subject.trim()
        ? `${form.message.trim()}\n\n— Subject: ${form.subject.trim()}`
        : form.message.trim();
      await submitContactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        message: combined,
      });
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Could not send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      {sent && (
        <Success>
          Message received — we&apos;ll review it shortly. Thank you for reaching out.
        </Success>
      )}
      {error ? <Err>{error}</Err> : null}
      <form onSubmit={onSubmit}>
        <Grid2>
          <div>
            <Label htmlFor="c-name">Name *</Label>
            <Input
              id="c-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Your name"
              autoComplete="name"
            />
          </div>
          <div>
            <Label htmlFor="c-email">Email *</Label>
            <Input
              id="c-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              autoComplete="email"
            />
          </div>
        </Grid2>
        <Label htmlFor="c-subject">Subject</Label>
        <Input
          id="c-subject"
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder="How can we help?"
        />
        <Label htmlFor="c-msg">Message *</Label>
        <Textarea
          id="c-msg"
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Your message..."
        />
        <Submit type="submit" disabled={loading}>
          {loading ? "Sending…" : "Send message"}
        </Submit>
        <Note>Messages are stored for staff review. For urgent issues, mention that in the text.</Note>
      </form>
    </Card>
  );
}
